import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { statsApi } from '../features/stats/api.ts';
import { dv } from '../i18n/dv.ts';
import { displayName } from '../lib/utils.ts';
import { type Body, bodyOfRole } from '../lib/body.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';
import { useAuthStore } from '../store/auth.ts';

type Preset = 'm3' | 'm6' | 'y1' | 'custom';

// Compute an ISO from/to for a preset (now back N months -> now).
function presetRange(preset: Exclude<Preset, 'custom'>): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  if (preset === 'm3') from.setMonth(from.getMonth() - 3);
  else if (preset === 'm6') from.setMonth(from.getMonth() - 6);
  else from.setFullYear(from.getFullYear() - 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

// YYYY-MM-DD (local) for a date input value.
function toDateInput(iso: string): string {
  const d = new Date(iso);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// «އެޖެންޑާ ހުށަހެޅުންތައް» — who submitted how many agenda items in a chosen
// period, shown as a horizontal stacked bar chart (approved / rejected /
// pending) per submitter.
export function StatsSubmissions(): JSX.Element {
  // Default to the viewer's own body when they're elected, else COUNCIL.
  const role = useAuthStore((s) => s.user?.role);
  const [body, setBody] = useState<Body>(() => (role ? bodyOfRole(role) : null) ?? 'COUNCIL');
  const [preset, setPreset] = useState<Preset>('m3');
  const initial = useMemo(() => presetRange('m3'), []);
  const [customFrom, setCustomFrom] = useState(() => toDateInput(initial.from));
  const [customTo, setCustomTo] = useState(() => toDateInput(initial.to));

  const range = useMemo(() => {
    if (preset === 'custom') {
      const from = new Date(`${customFrom}T00:00:00`);
      const to = new Date(`${customTo}T23:59:59`);
      return { from: from.toISOString(), to: to.toISOString() };
    }
    return presetRange(preset);
  }, [preset, customFrom, customTo]);

  const validCustom = preset !== 'custom' || (customFrom && customTo && customFrom <= customTo);

  const q = useQuery({
    queryKey: ['stats', 'submissions', range.from, range.to, body],
    queryFn: () => statsApi.submissions(range.from, range.to, body),
    enabled: !!validCustom,
  });

  const rows = q.data?.bySubmitter ?? [];
  const max = rows.reduce((mx, r) => Math.max(mx, r.total), 0) || 1;

  return (
    <div className="space-y-6">
      {/* Toolbar — body + period filters (+ inline custom range). When «އިޚްތިޔާރު»
          is picked, the from/to dates sit on the same row as the other filters. */}
      <div className="flex flex-wrap items-center gap-2 rounded-3xl bg-[hsl(var(--chrome))] p-3 sm:px-4">
        <BodyToggle body={body} onChange={setBody} />
        <select
          aria-label={dv.stats.submissionsHeading}
          value={preset}
          onChange={(e) => setPreset(e.target.value as Preset)}
          className="select w-auto rounded-full py-1.5"
        >
          {(['m3', 'm6', 'y1', 'custom'] as const).map((p) => (
            <option key={p} value={p}>{dv.stats.period[p]}</option>
          ))}
        </select>
        {preset === 'custom' && (
          <>
            <input
              type="date"
              dir="ltr"
              aria-label={dv.stats.period.from}
              value={customFrom}
              max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="input w-auto rounded-full py-1.5"
            />
            <span className="text-sm text-muted-foreground">–</span>
            <input
              type="date"
              dir="ltr"
              aria-label={dv.stats.period.to}
              value={customTo}
              min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="input w-auto rounded-full py-1.5"
            />
          </>
        )}
      </div>

      {q.isLoading ? (
        <p className="rounded-3xl bg-[hsl(var(--chrome))] p-6 text-sm text-muted-foreground" role="status" aria-live="polite">
          {dv.common.loading}
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-3xl bg-[hsl(var(--chrome))] p-8 text-center text-sm text-muted-foreground">{dv.stats.noData}</p>
      ) : (
        <section className="space-y-5 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
          {/* Total + legend */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-baseline gap-2">
              <span className="tabular text-3xl font-bold text-foreground" dir="ltr">{q.data!.total}</span>
              <span className="text-sm text-muted-foreground">{dv.stats.totalSubmissions}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><i className="size-2.5 rounded-full bg-[hsl(var(--success))]" />{dv.stats.approved}</span>
              <span className="flex items-center gap-1.5"><i className="size-2.5 rounded-full bg-[hsl(var(--destructive))]" />{dv.stats.rejected}</span>
              <span className="flex items-center gap-1.5"><i className="size-2.5 rounded-full bg-[hsl(var(--warning))]" />{dv.stats.pending}</span>
            </div>
          </div>

          {/* Bar chart — one row per submitter, as an M3 list with a leading
              tonal person chip and a slim segmented bar. */}
          <ul className="space-y-2">
            {rows.map((s) => (
              <li key={s.userId} className="flex items-center gap-3.5">
                <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                  <Icon name="person" size={22} />
                </span>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate font-medium">{displayName({ name: s.name, nameDv: s.nameDv }) || s.userId}</span>
                    <span className="tabular shrink-0 font-semibold" dir="ltr">{s.total}</span>
                  </div>
                  <div className="h-3.5 overflow-hidden rounded-full bg-[hsl(var(--surface-container-highest))]" role="img"
                    aria-label={`${displayName({ name: s.name, nameDv: s.nameDv })}: ${s.total}`}>
                    <div className="flex h-full overflow-hidden rounded-full" style={{ width: `${(s.total / max) * 100}%` }}>
                      {s.approved > 0 && <div className="h-full bg-[hsl(var(--success))]" style={{ flexGrow: s.approved }} />}
                      {s.rejected > 0 && <div className="h-full bg-[hsl(var(--destructive))]" style={{ flexGrow: s.rejected }} />}
                      {s.pending > 0 && <div className="h-full bg-[hsl(var(--warning))]" style={{ flexGrow: s.pending }} />}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
